// src/oura/components/onboarding/BaselineSnapshot.tsx
//
// The Baseline Snapshot on the Onboarding "Send results and plan" card
// (#4423): a row that follows Oura making it ("Oura makes it once the results
// are published" → "Making it" → "Open"), and the drawer that draws it with
// the same sections as the PDF the team made by hand: the header, 4 stat
// cards, the 3-check band, the callout, the question table with each engine's
// cell and the rivals named instead, the star legend and the notes.
//
// Every word and number comes from the backend (ourteam-backend#6203); this
// file only lays them out. The row is not one of the three things the Send
// button waits for: the email still carries the uploaded baseline report.

import { useEffect, useState, type ReactNode } from "react";
import { CheckIcon, Loader2Icon, StarIcon } from "lucide-react";
import { Drawer, PillButton, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";
import { dayMonth } from "../../onboarding/derive";
import type { BaselineSnapshot, SnapshotCell } from "@service/aeo/baselineSnapshot";
import { useBaselineSnapshot } from "./useBaselineSnapshot";

/** The row's marker, as the card's other rows draw theirs: a green tick once there is something to open. */
function Marker({ done }: { done: boolean }) {
    return (
        <span aria-hidden className="flex w-5 shrink-0 justify-center">
            {done ? (
                <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white">
                    <CheckIcon size={12} strokeWidth={3} />
                </span>
            ) : (
                <span className="grid h-5 w-5 place-items-center rounded-full border-2 border-blue-600 bg-white">
                    <span className="h-2 w-2 rounded-full bg-blue-600" />
                </span>
            )}
        </span>
    );
}

/** The Baseline Snapshot's row on the results card, and its drawer. */
export function BaselineSnapshotRow({
    assessmentId,
    businessName,
    onReady,
}: {
    assessmentId: string;
    businessName: string;
    /** When it is ready to open (its date, or now when the backend gives none), or null: the results card's Send waits on it. */
    onReady?: (at: string | null) => void;
}) {
    const { load, starting, refused, read, make } = useBaselineSnapshot(assessmentId);
    const [open, setOpen] = useState(false);
    const snap = load.state === "read" ? load.read : null;
    const ready = snap?.status === "ready" && !!snap.data;
    const readyAt = ready ? snap?.at ?? new Date().toISOString() : null;
    // Only whether it is ready, and its date, are told: not every read.
    const readyKey = ready ? snap?.at ?? "ready" : null;
    useEffect(() => {
        onReady?.(readyAt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readyKey]);

    let right: ReactNode;
    let fact: string | null = null;
    if (load.state === "loading") {
        right = <span className={cn(TYPE.tc, "shrink-0 text-gray-500")}>Reading…</span>;
    } else if (load.state === "failed") {
        fact = load.message;
        right = <PillButton size="sm" onClick={() => void read()}>Try again</PillButton>;
    } else if (snap?.status === "making") {
        right = (
            <span role="status" className={cn(TYPE.tc, "inline-flex shrink-0 items-center gap-1.5 text-gray-600")}>
                <Loader2Icon aria-hidden size={14} className="animate-spin motion-reduce:animate-none" />
                Making it
            </span>
        );
    } else if (ready) {
        fact = snap?.at ? `Made ${dayMonth(snap.at)}` : null;
        right = <PillButton size="sm" onClick={() => setOpen(true)}>Open</PillButton>;
    } else if (snap?.status === "failed") {
        fact = snap.error ? `It stopped: ${snap.error}` : "It stopped before it finished.";
        right = <PillButton size="sm" busy={starting} writes onClick={() => void make()}>Try again</PillButton>;
    } else {
        fact = "Oura makes it once the results are published.";
        right = <PillButton size="sm" busy={starting} writes onClick={() => void make()}>Make it now</PillButton>;
    }

    return (
        <li className="flex min-h-12 flex-wrap items-center gap-3 py-2">
            <Marker done={ready} />
            <span className="min-w-0 flex-1">
                <span className={cn(TYPE.tb, "block text-gray-900")}>
                    Baseline Snapshot
                    <span className="sr-only">{ready ? ", ready" : ", not ready"}</span>
                </span>
                <span className={cn(TYPE.tm, "block text-gray-500")}>Each of the 50 questions and what AI said, made by Oura from the first check</span>
                {(fact || refused) && (
                    <span className={cn(TYPE.tm, "mt-0.5 block", refused || load.state === "failed" || snap?.status === "failed" ? "text-rose-700" : "text-gray-500")}>
                        {refused ?? fact}
                    </span>
                )}
            </span>
            {right}
            {ready && snap?.data && (
                <Drawer open={open} onClose={() => setOpen(false)} title="Baseline Snapshot" sub={businessName} width={1040}>
                    <SnapshotView snap={snap.data} />
                </Drawer>
            )}
        </li>
    );
}

/** One engine's cell: green when named, grey when that engine has no answer stored, plain otherwise. */
function Cell({ cell }: { cell: SnapshotCell | undefined }) {
    if (!cell) return <span className="text-gray-400">—</span>;
    if (cell.available === false) return <span className="italic text-gray-400">{cell.label || "not read"}</span>;
    return <span className={cell.named ? "font-semibold text-emerald-700" : "text-gray-500"}>{cell.label}</span>;
}

/** The snapshot, section by section, in the PDF's order. */
export function SnapshotView({ snap }: { snap: BaselineSnapshot }) {
    const columns = snap.columns?.length ? snap.columns : ["#", "Question", ...snap.engines.map((e) => e.label), "Named instead"];
    // The simplified top (Jack, 20 Oct 2026): one headline in questions, check cards in questions only.
    const headline = snap.headline ?? null;
    return (
        <div className="flex flex-col gap-6">
            <header className="flex flex-col gap-1">
                {snap.meta && <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{snap.meta}</p>}
                <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>{snap.title}</h2>
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                    {[snap.business.name, snap.business.domain, snap.date].filter(Boolean).join(" · ")}
                </p>
            </header>

            {headline && (
                <div className="flex flex-col gap-2">
                    {headline.intro && <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{headline.intro}</p>}
                    <div className="flex flex-col gap-1 rounded-2xl bg-blue-600 p-6 text-white">
                        <span className="text-[32px] font-semibold leading-[38px] tracking-[-0.02em] tabular-nums text-white">{headline.value}</span>
                        <span className={cn(TYPE.tb, "text-white")}>{headline.label}</span>
                        {headline.engines && <span className={cn(TYPE.tc, "mt-1 tabular-nums text-white")}>{headline.engines}</span>}
                        {headline.rival && <span className={cn(TYPE.tc, "text-white")}>{headline.rival}</span>}
                    </div>
                </div>
            )}

            {headline && snap.checks.length > 0 && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    {snap.checks.map((c) => (
                        <div key={c.check} className="flex flex-col gap-1 rounded-xl border border-gray-200 p-4">
                            <span className={TYPE.lab}>{c.title}</span>
                            <span className={cn(TYPE.num, "text-gray-900")}>{c.namedQuestions} of {c.questions}</span>
                            <span className={cn(TYPE.tc, "text-gray-500")}>{c.question}</span>
                        </div>
                    ))}
                </div>
            )}

            {!headline && snap.stats.length > 0 && (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    {snap.stats.map((s) => (
                        <div key={s.label} className="flex flex-col gap-1 rounded-xl bg-gray-50 p-4">
                            <span className="text-2xl font-semibold leading-none tabular-nums text-gray-900">{s.value}</span>
                            <span className={cn(TYPE.tc, "text-gray-600")}>{s.label}</span>
                        </div>
                    ))}
                </div>
            )}

            {!headline && snap.checks.length > 0 && (
                <section className="flex flex-col gap-3">
                    {snap.checkBand?.heading && <h3 className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>{snap.checkBand.heading}</h3>}
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        {snap.checks.map((c) => (
                            <div key={c.check} className="flex flex-col gap-1 rounded-xl border border-gray-200 p-4">
                                <span className={cn(TYPE.tb, "font-semibold text-gray-900")}>{c.title}</span>
                                <span className={cn(TYPE.tc, "text-gray-500")}>{c.question}</span>
                                <span className="mt-1 text-xl font-semibold tabular-nums text-gray-900">{c.pct}%</span>
                                <span className={cn(TYPE.tc, "tabular-nums text-gray-600")}>
                                    {c.named} of {c.of} answers · {c.namedQuestions} of {c.questions} questions
                                </span>
                            </div>
                        ))}
                    </div>
                    {snap.checkBand?.legend?.map((line) => (
                        <p key={line} className={cn(TYPE.tc, "m-0 text-gray-500")}>{line}</p>
                    ))}
                </section>
            )}

            {snap.callout && (headline
                ? <p className={cn(TYPE.tb, "m-0 text-gray-700")}>{snap.callout}</p>
                : <p className={cn(TYPE.tb, "m-0 rounded-xl border-l-4 border-blue-600 bg-blue-50 px-4 py-3 text-gray-900")}>{snap.callout}</p>)}
            {snap.lead && <p className={cn(TYPE.tb, "m-0 text-gray-700")}>{snap.lead}</p>}

            <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full min-w-[760px] border-collapse text-left">
                    <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                            {columns.map((c) => (
                                <th key={c} className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-500">{c}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {snap.rows.map((r) => (
                            // A row out of the total (nobody was named, or it does not count) is greyed, never hidden.
                            <tr key={r.n} className={cn("align-top", !r.counted && "bg-gray-50 text-gray-400")}>
                                <td className={cn(TYPE.tc, "w-px whitespace-nowrap px-3 py-2.5 tabular-nums")}>{r.n}</td>
                                <td className={cn(TYPE.tc, "px-3 py-2.5", r.counted ? "text-gray-900" : "text-gray-400")}>
                                    {r.local && (
                                        <StarIcon aria-label="Location question" size={12} className="mr-1 inline-block fill-amber-400 align-[-1px] text-amber-400" />
                                    )}
                                    {r.question}
                                    {r.nobodyNamed && <span className="ml-1 text-gray-400">(no assistant named anyone)</span>}
                                </td>
                                {snap.engines.map((e) => (
                                    <td key={e.key} className={cn(TYPE.tc, "whitespace-nowrap px-3 py-2.5")}>
                                        <Cell cell={r.cells[e.key]} />
                                    </td>
                                ))}
                                <td className={cn(TYPE.tc, "px-3 py-2.5", r.counted ? "text-gray-600" : "text-gray-400")}>
                                    {r.rivals.length > 0 ? r.rivals.join(", ") : "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {snap.legendText && <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{snap.legendText}</p>}

            {snap.notes.length > 0 && (
                <section className="flex flex-col gap-2">
                    {snap.notesHeading && <h3 className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>{snap.notesHeading}</h3>}
                    <ul className="m-0 flex list-disc flex-col gap-1.5 pl-5">
                        {snap.notes.map((n) => (
                            <li key={n} className={cn(TYPE.tc, "text-gray-700")}>{n}</li>
                        ))}
                    </ul>
                </section>
            )}

            {snap.footer && <p className={cn(TYPE.tm, "m-0 border-t border-gray-100 pt-3 text-gray-400")}>{snap.footer}</p>}
        </div>
    );
}
